import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { cn } from '@/components/ui/cn';
import { track } from '@/features/analytics';
import { useQuizStore, nextVisibleStep } from '../state';
import { BUDGET_BUCKETS } from '../schema';
import type { Answer, BudgetBucket, Question } from '../types';
import { getSomaticEcho } from '../somatic-echo';
import { ChipSelect } from './ChipSelect';
import { BudgetSlider } from './BudgetSlider';
import { ImagePicker } from './ImagePicker';
import { GlossText } from './GlossText';

interface SomaticEchoProps {
  question: Question;
  answer: Answer | undefined;
}

function SomaticEcho({ question, answer }: SomaticEchoProps) {
  const { t } = useTranslation();
  const echo = useMemo(() => getSomaticEcho(question, answer, t as (key: string, opts?: Record<string, unknown>) => string), [question, answer, t]);
  if (!echo) return null;
  return (
    <div
      key={echo}
      className="mt-6 flex items-start gap-2"
      style={{ animation: 'rmFadeIn 240ms ease-out' }}
    >
      <span aria-hidden="true" className="mt-0.5 shrink-0 text-[10px] text-terra-600">✓</span>
      <p className="font-mono text-[11px] leading-relaxed text-ink-500">{echo}</p>
    </div>
  );
}

export interface QuestionStepProps {
  question: Question;
  /** Called when Next is clicked on the last step. */
  onComplete?: () => void;
  className?: string;
}

function isAnswerEmpty(answer: Answer | undefined, question: Question): boolean {
  if (!answer) return true;
  if (question.type === 'budget-slider') return answer.budgetBucket == null;
  return answer.optionIds.length === 0;
}

/**
 * Composes a single quiz step in the Quiet Atlas language:
 *   - serif h2 question title, ink-500 description
 *   - body (Chip/Budget/Image), terra-accented selection
 *   - footer: ghost Back · ghost Skip · terra Continue
 *
 * Reads + writes the quiz store directly. Visual chrome is bare — no Card
 * wrapper, just the editorial rhythm dictated by paper bg / generous spacing.
 */
export function QuestionStep({ question, onComplete, className }: QuestionStepProps) {
  const { t } = useTranslation();
  const currentStep = useQuizStore((s) => s.currentStep);
  const answers = useQuizStore((s) => s.answers);
  const setAnswer = useQuizStore((s) => s.setAnswer);
  const next = useQuizStore((s) => s.next);
  const prev = useQuizStore((s) => s.prev);

  const answer = answers[question.id];
  // "Last visible step" — clicking Next would not move us, so the button
  // becomes Finish. Computed against the current answer set so hidden
  // questions are skipped just like in next().
  const isLast = useMemo(
    () => nextVisibleStep(currentStep, answers) === currentStep,
    [currentStep, answers],
  );
  const empty = useMemo(() => isAnswerEmpty(answer, question), [answer, question]);

  const setOptionIds = (optionIds: string[]) => {
    setAnswer(question.id, { questionId: question.id, optionIds });
  };

  const setBudget = (bucket: BudgetBucket | null) => {
    setAnswer(question.id, {
      questionId: question.id,
      optionIds: [],
      budgetBucket: bucket ?? undefined,
    });
  };

  const handleNext = () => {
    if (!question.skippable && empty) return;
    // CV.PoL — Career-intent gate telemetry (M0). Fires once when the user
    // advances past the question with an answer. Skip-advance is handled in
    // handleSkip and is tagged as 'skipped' (≡ 'none' for downstream analysis).
    if (question.id === 'career_intent') {
      const optionId = answer?.optionIds[0] ?? 'skipped';
      track('career_intent_answered', { option: optionId });
    }
    if (isLast) {
      onComplete?.();
      return;
    }
    next();
  };

  const handleSkip = () => {
    if (question.id === 'career_intent') {
      track('career_intent_answered', { option: 'skipped' });
    }
    if (isLast) {
      onComplete?.();
      return;
    }
    next();
  };

  const renderBody = () => {
    if (question.type === 'budget-slider') {
      return (
        <BudgetSlider
          value={answer?.budgetBucket ?? null}
          onChange={setBudget}
          buckets={BUDGET_BUCKETS}
        />
      );
    }
    if (question.id === 'climate') {
      return (
        <ImagePicker
          question={question}
          value={answer?.optionIds ?? []}
          onChange={setOptionIds}
          max={question.multi_max ?? 2}
        />
      );
    }
    return (
      <ChipSelect
        question={question}
        value={answer?.optionIds ?? []}
        onChange={setOptionIds}
        max={question.multi_max}
      />
    );
  };

  const nextDisabled = !question.skippable && empty;

  return (
    <section className={cn('w-full', className)}>
      <header>
        <h2 className="font-display text-3xl font-medium leading-tight tracking-tight text-ink-900 sm:text-[36px]">
          {t(question.label_key)}
        </h2>
        {question.description_key && (
          <GlossText
            i18nKey={question.description_key}
            className="mt-3 block max-w-xl text-base leading-relaxed text-ink-500"
          />
        )}
        {question.type === 'multi' && (
          <p className="mt-2 font-mono text-[11px] uppercase tracking-wider text-ink-400">
            {question.multi_max
              ? t('quiz.hint.selectUpTo', { n: question.multi_max })
              : t('quiz.hint.selectAll')}
          </p>
        )}
      </header>

      <div className="mt-8">{renderBody()}</div>

      {/* Somatic echo — Layer 3: shows the system understood the answer in human terms */}
      <SomaticEcho question={question} answer={answer} />

      <footer className="mt-10 flex flex-wrap items-center justify-between gap-2 border-t border-ink-100 pt-6">
        {currentStep > 0 ? (
          <Button
            variant="ghost"
            onClick={prev}
            aria-label={t('quiz.nav.back')}
          >
            <span aria-hidden="true" className="font-mono text-xs opacity-80">
              ←
            </span>
            {t('quiz.nav.back')}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          {question.skippable && (
            <Button variant="ghost" onClick={handleSkip} aria-label={t('quiz.nav.skip')}>
              {t('quiz.nav.skip')}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={nextDisabled}
            aria-label={isLast ? t('quiz.nav.finish') : t('quiz.nav.next')}
          >
            {isLast ? t('quiz.nav.finish') : t('quiz.nav.next')}
            <span aria-hidden="true" className="font-mono text-xs opacity-80">
              →
            </span>
          </Button>
        </div>
      </footer>
    </section>
  );
}
